import { supabase } from '../../lib/supabase';

// Define the type for the sublease data
export interface Sublease {
  lease_id: number;
  lease_holder: number | null;
  leaseholder_name: string | null;
  lease_start: string | null;
  lease_end: string | null;
  lease_method: string | null;
  lease_type: string | null;
  lease_furth_term: string | null;
  lease_rent_freq: string | null;
  lease_dealing: string | null;
  lease_deal_doc: string | null;
}

export const supabaseSubleaseService = {
  // Get all subleases with leaseholder information
  getAll: async (): Promise<Sublease[]> => {
    console.log('Fetching all subleases with leaseholder information...');
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    console.log('Authentication session:', session ? 'Valid session' : 'No session');
    
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    try {
      console.log('Executing Supabase query on subleases table...');
      
      // Query subleases with join to leaseholders
      const { data, error } = await supabase
        .from('subleases')
        .select(`
          lease_id,
          lease_holder,
          lease_start,
          lease_end,
          lease_method,
          lease_type,
          lease_furth_term,
          lease_rent_freq,
          lease_dealing,
          lease_deal_doc,
          leaseholders (
            holder_id,
            holder_name
          )
        `)
        .order('lease_id', { ascending: true });
      
      console.log('Supabase query executed');
      
      if (error) {
        console.error('Error fetching subleases:', error);
        return [];
      }
      
      if (!data || data.length === 0) {
        console.log('No subleases found');
        return [];
      }
      
      console.log(`Found ${data.length} subleases - raw data sample:`, data.slice(0, 3));
      
      // Transform the data to include leaseholder_name directly
      const subleases: Sublease[] = data.map(sublease => {
        // Handle the case where leaseholders might be null or an array
        let leaseholderName = null;
        if (sublease.leaseholders) {
          if (Array.isArray(sublease.leaseholders) && sublease.leaseholders.length > 0) {
            leaseholderName = sublease.leaseholders[0].holder_name;
          } else if (typeof sublease.leaseholders === 'object') {
            leaseholderName = (sublease.leaseholders as any).holder_name;
          }
        }
        
        return {
          lease_id: sublease.lease_id,
          lease_holder: sublease.lease_holder,
          leaseholder_name: leaseholderName,
          lease_start: sublease.lease_start,
          lease_end: sublease.lease_end,
          lease_method: sublease.lease_method,
          lease_type: sublease.lease_type,
          lease_furth_term: sublease.lease_furth_term,
          lease_rent_freq: sublease.lease_rent_freq,
          lease_dealing: sublease.lease_dealing,
          lease_deal_doc: sublease.lease_deal_doc
        };
      });
      
      console.log(`Transformed ${subleases.length} subleases with leaseholder names`);
      return subleases;
    } catch (error) {
      console.error('Unexpected error in getAll:', error);
      return [];
    }
  },
  
  // Get sublease by ID
  getById: async (id: number): Promise<Sublease | null> => {
    console.log(`Fetching sublease with ID ${id}...`);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return null;
    }
    
    try {
      // Query sublease with join to leaseholders
      const { data, error } = await supabase
        .from('subleases')
        .select(`
          lease_id,
          lease_holder,
          lease_start,
          lease_end,
          lease_method,
          lease_type,
          lease_furth_term,
          lease_rent_freq,
          lease_dealing,
          lease_deal_doc,
          leaseholders (
            holder_id,
            holder_name
          )
        `)
        .eq('lease_id', id)
        .single();
      
      if (error) {
        console.error(`Error fetching sublease with ID ${id}:`, error);
        return null;
      }
      
      if (!data) {
        console.log(`No sublease found with ID ${id}`);
        return null;
      }
      
      // Handle the case where leaseholders might be null or an array
      let leaseholderName = null;
      if (data.leaseholders) {
        if (Array.isArray(data.leaseholders) && data.leaseholders.length > 0) {
          leaseholderName = data.leaseholders[0].holder_name;
        } else if (typeof data.leaseholders === 'object') {
          leaseholderName = (data.leaseholders as any).holder_name;
        }
      }
      
      return {
        lease_id: data.lease_id,
        lease_holder: data.lease_holder,
        leaseholder_name: leaseholderName,
        lease_start: data.lease_start,
        lease_end: data.lease_end,
        lease_method: data.lease_method,
        lease_type: data.lease_type,
        lease_furth_term: data.lease_furth_term,
        lease_rent_freq: data.lease_rent_freq,
        lease_dealing: data.lease_dealing,
        lease_deal_doc: data.lease_deal_doc
      };
    } catch (error) {
      console.error(`Unexpected error in getById for ID ${id}:`, error);
      return null;
    }
  },
  
  // Get just the lease_ids for dropdowns
  getSubleaseIds: async (): Promise<number[]> => {
    console.log('Fetching sublease IDs for dropdown...');
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    try {
      // Simple direct query to get just the lease_id
      const { data, error } = await supabase
        .from('subleases')
        .select('lease_id');

      if (error) {
        console.error('Error fetching sublease IDs:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log('No subleases found');
        return [];
      }

      // Return just the lease_ids
      return data.map(sublease => sublease.lease_id);
    } catch (error) {
      console.error('Error in getSubleaseIds:', error);
      return [];
    }
  }
};
